import { Building2, Eye, History, RefreshCw, TrendingUp } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { formatCurrency } from '@/utils/format-currency'
import { formatDate } from '@/utils/format-date'
import type { Deal } from '@/types'

type Props = {
  deals: Deal[]
  onReopen: (dealId: string) => void
}

export function RocketSearchHistoryPanel({ deals, onReopen }: Props) {
  return (
    <div className="space-y-4">
      <div className="glass-card p-5">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-emerald-400" />
          <h3 className="font-semibold text-[var(--font-color-h3)]" style={{ fontSize: 'var(--font-size-h3)' }}>
            Negociações Encerradas
          </h3>
          <Badge variant="muted">{deals.length}</Badge>
        </div>
      </div>

      {deals.length === 0 ? (
        <div className="glass-card text-center py-12">
          <History size={32} className="mx-auto text-[var(--font-color-muted)] mb-2 opacity-50" />
          <p className="text-sm text-[var(--font-color-muted)] italic">Nenhuma negociação encerrada.</p>
        </div>
      ) : (
        <div className="glass-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--glass-border)]">
                  {['Empresa', 'Valor', 'Resultado', 'Prob. Final', 'Closer', 'Fechamento', 'Ações'].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider whitespace-nowrap"
                      style={{ fontSize: 'var(--font-size-label)' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {deals.map((deal) => (
                  <tr
                    key={deal.id}
                    className="border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 size={13} className="text-[var(--brand-primary)] shrink-0" />
                        <span className="text-[13px] font-medium text-[var(--font-color-body)]">
                          {deal.empresaNome}
                        </span>
                        {deal.proposalViewedAt && (
                          <span title="Proposta visualizada">
                            <Eye size={11} className="text-emerald-400 shrink-0" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[13px] font-semibold text-[var(--font-color-h1)]">
                        {formatCurrency(deal.valor)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {deal.etapa === 'ganho' ? (
                        <Badge variant="success">Ganho</Badge>
                      ) : (
                        <Badge variant="danger">Perdido</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[12px] text-[var(--font-color-muted)]">
                        {deal.probabilidade || 0}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[12px] text-[var(--font-color-muted)]">{deal.closerNome}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[12px] text-[var(--font-color-muted)]">
                        {formatDate(deal.updatedAt)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<RefreshCw size={13} />}
                        onClick={() => onReopen(deal.id)}
                      >
                        Reabrir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
